import { buildPath } from "@contract";
import { useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiRequestError } from "../../../api/fetcher";
import { Button } from "../../../components/control/Button";
import { Banner } from "../../../components/feedback/Banner";
import { ErrorBanner } from "../../../components/feedback/ErrorBanner";
import { Bytes } from "../../../components/format/Bytes";
import { ConfirmDialog } from "../../../components/overlay/ConfirmDialog";
import { useBookContext } from "../bookContext";
import { useCoverChange } from "./lifecycle";

const MAX_COVER_BYTES = 32 * 1024 * 1024;
export function BookCover() {
  const { book } = useBookContext();
  const { t } = useTranslation("overview");
  const change = useCoverChange(book.id);
  const input = useRef<HTMLInputElement>(null);
  const hintId = useId();
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [validation, setValidation] = useState<"format" | "size" | null>(null);
  const [saved, setSaved] = useState(false);
  const formatError =
    change.error instanceof ApiRequestError && change.error.code === "UNSUPPORTED_FORMAT";
  const sizeError =
    change.error instanceof ApiRequestError && change.error.code === "PAYLOAD_TOO_LARGE";
  const feedback = validation ?? (formatError ? "format" : sizeError ? "size" : null);
  const error = feedback ? (
    <Banner tone="danger">
      {feedback === "format" ? t("cover.invalidFormat") : t("cover.tooLarge")}
    </Banner>
  ) : (
    <ErrorBanner error={change.error} />
  );
  const coverUrl = book.cover
    ? `/api${buildPath("/books/:id/cover", { id: book.id })}?v=${encodeURIComponent(book.cover.updatedAt)}`
    : undefined;
  return (
    <div className="flex min-w-0 flex-col gap-3 border-t border-line-soft pt-4">
      <h3 className="label-mono">{t("cover.title")}</h3>
      <div className="flex min-w-0 items-start gap-3">
        {book.cover ? (
          <img
            src={coverUrl}
            alt={t("cover.alt", { title: book.title ?? t("book.untitled") })}
            className="h-24 w-16 shrink-0 border border-line-soft object-contain"
          />
        ) : null}
        <div className="flex min-w-0 flex-1 flex-col items-start gap-2">
          <p className="text-data text-ink-2">
            {book.cover ? <Bytes value={book.cover.sizeBytes} /> : t("cover.empty")}
          </p>
          <Button
            size="sm"
            variant="secondary"
            busy={change.isPending}
            onClick={() => input.current?.click()}
          >
            {book.cover ? t("cover.replace") : t("cover.upload")}
          </Button>
          {book.cover ? (
            <Button
              size="sm"
              variant="quiet"
              disabled={change.isPending}
              onClick={() => {
                change.reset();
                setValidation(null);
                setSaved(false);
                setConfirmRemove(true);
              }}
            >
              {t("cover.remove")}
            </Button>
          ) : null}
        </div>
      </div>
      <input
        ref={input}
        type="file"
        accept="image/jpeg,image/png,.jpg,.jpeg,.png"
        className="hidden"
        aria-label={t("cover.choose")}
        aria-describedby={hintId}
        disabled={change.isPending}
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = "";
          if (!file) return;
          change.reset();
          setSaved(false);
          const invalid =
            file.size > MAX_COVER_BYTES
              ? "size"
              : !/\.(jpe?g|png)$/i.test(file.name) && !/^image\/(jpeg|png)$/.test(file.type)
                ? "format"
                : null;
          setValidation(invalid);
          if (invalid === null) change.mutate(file, { onSuccess: () => setSaved(true) });
        }}
      />
      <p id={hintId} className="text-data text-ink-3">
        {t("cover.hint")}
      </p>
      {confirmRemove ? null : error}
      {saved ? (
        <p role="status" className="text-data text-ink-2">
          {t("cover.saved")}
        </p>
      ) : null}
      <ConfirmDialog
        open={confirmRemove}
        title={t("cover.removeTitle")}
        confirmLabel={t("cover.remove")}
        tone="danger"
        busy={change.isPending}
        onCancel={() => setConfirmRemove(false)}
        onConfirm={() =>
          change.mutate(null, {
            onSuccess: () => {
              setConfirmRemove(false);
              setSaved(true);
            },
          })
        }
      >
        <div className="flex flex-col gap-3">
          <p>{t("cover.removeBody")}</p>
          {error}
        </div>
      </ConfirmDialog>
    </div>
  );
}
